import { Injectable } from '@angular/core';
import { SocketService, SocketSubscription } from '@mode/shared/data-access-socket';
import { ToastService } from '@mode/shared/util-toast';
import { createEffect, Actions, ofType } from '@ngrx/effects';
import { GoogleSheetSyncFailureToastComponent } from '@mode/dataset/google/util';
import { mergeMap, switchMap, catchError, of, tap, filter, map, concatMap, distinctUntilChanged } from 'rxjs';
import { GoogleSheetRunEvents, GoogleSheetRunPusherSuccessPayload } from '@mode/dataset/google/contract';
import { GoogleSheetSyncClientService } from './google-sheet-sync-client.service';
import * as GoogleSheetSyncActions from './google-sheet-sync.actions';
import { SwitchViewFacade } from '@mode/switch-views/contract';
import { DatasetFacade } from '@mode/dataset/contract';

@Injectable()
export class GoogleSheetSyncEffects {
  syncGoogleSheet$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GoogleSheetSyncActions.datasetEditorSyncGoogleSheet),
      switchMap(({ accountId, datasetId }) =>
        this.googleSheetSyncService.syncGoogleSheetDataset(accountId, datasetId).pipe(
          map((response) => {
            return GoogleSheetSyncActions.startGoogleSheetSyncSuccess({
              channelName: response.pusher_channel_name,
            });
          })
        )
      ),
      catchError((error) => {
        console.error('Error', error);
        return of(GoogleSheetSyncActions.syncGoogleSheetFailure({ error }));
      })
    )
  );

  startGoogleSheetSyncSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GoogleSheetSyncActions.startGoogleSheetSyncSuccess),
      map(({ channelName }) => {
        let googleSheetSubscription = {} as SocketSubscription<GoogleSheetRunPusherSuccessPayload>;
        try {
          googleSheetSubscription = this.socketService.connect<GoogleSheetRunPusherSuccessPayload>(channelName);
        } catch (error: any) {
          GoogleSheetSyncActions.syncGoogleSheetFailure({ error });
          throw error;
        }
        return GoogleSheetSyncActions.googleSheetSyncSocketConnectionSuccess({
          googleSheetSyncSubscription: googleSheetSubscription,
        });
      })
    )
  );

  googleSheetSyncSocketConnectionSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GoogleSheetSyncActions.googleSheetSyncSocketConnectionSuccess),
      mergeMap(({ googleSheetSyncSubscription }) =>
        googleSheetSyncSubscription.eventBus.pipe(
          filter((event) => Object.values(GoogleSheetRunEvents).includes(event.name as GoogleSheetRunEvents)),
          map((event) => {
            switch (event.name) {
              case GoogleSheetRunEvents.GoogleSheetSuccess:
                googleSheetSyncSubscription.disconnect();
                return GoogleSheetSyncActions.syncGoogleSheetSuccess();
              case GoogleSheetRunEvents.GoogleSheetFailed:
                googleSheetSyncSubscription.disconnect();
                return GoogleSheetSyncActions.syncGoogleSheetFailure({ error: 'Google Sheet Ingest Failed' });
            }
            return GoogleSheetSyncActions.googleSheetSyncEnqueued();
          })
        )
      )
    )
  );

  syncGoogleSheetSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GoogleSheetSyncActions.syncGoogleSheetSuccess),
      tap(() => {
        this.toastService.success('Sync succeeded');
      }),
      map(() => GoogleSheetSyncActions.refreshGoogleSheetDataset())
    )
  );

  syncGoogleSheetFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(GoogleSheetSyncActions.syncGoogleSheetFailure),
        tap(() => {
          this.toastService.error('', {
            tapToDismiss: false,
            toastComponent: GoogleSheetSyncFailureToastComponent,
          });
        })
      ),
    { dispatch: false }
  );

  refreshGoogleSheetDataset$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(GoogleSheetSyncActions.refreshGoogleSheetDataset),
        switchMap(() =>
          this.datasetFacade.dataset$.pipe(
            map((dataset) => ({ datasetToken: dataset?.token, switchViewToken: dataset?.result?.switchViewToken })),
            distinctUntilChanged((old, notOld) => old.switchViewToken === notOld.switchViewToken)
          )
        ),
        tap(({ switchViewToken }) => this.switchViewFacade.getSwitchView(switchViewToken)),
        concatMap(({ datasetToken }) => {
          return this.switchViewFacade.getDataset(datasetToken);
        }),
        tap((dataView) => {
          this.switchViewFacade.googleDatasetSyncSwitchViewCompleted(dataView);
          this.datasetFacade.googleSheetSyncCompleted({ dataView });
        }),
        switchMap(() => this.datasetFacade.dataset$.pipe(map((dataset) => dataset?.result?.queryToken))),
        tap((queryToken) => this.switchViewFacade.queryRawDataset(queryToken))
      ),
    { dispatch: false }
  );

  constructor(
    private actions$: Actions,
    private googleSheetSyncService: GoogleSheetSyncClientService,
    private socketService: SocketService,
    private toastService: ToastService,
    private switchViewFacade: SwitchViewFacade,
    private datasetFacade: DatasetFacade
  ) {}
}
